import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Tickets } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getSettingsTickets,
  updateSettingsTickets,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox, Input } from '@/components/ui'

interface ITicketValues {
  numberOfAllowedTransfers: number
  upgradeInWalletEnabled: boolean
  showTicketCode: boolean
  dubaiIntegration: boolean
  dubaiChannel?: string | null
  dubaiSeller?: string | null
  dubaiPerformanceCode?: string | null
  ticketsEnabled: boolean
  redeemTicketsEnabled: boolean
  useZipPassword: boolean
}

export const TicketsComp: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [ticket, setTickets] = useState<Tickets>()
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsTickets()
        setTickets(resp.data)
        setValue('numberOfAllowedTransfers', resp.data.numberOfAllowedTransfers)
        setValue('upgradeInWalletEnabled', resp.data.upgradeInWalletEnabled)
        setValue('showTicketCode', resp.data.showTicketCode)
        setValue('dubaiIntegration', resp.data.dubaiIntegration)
        setValue('dubaiChannel', resp.data.dubaiChannel)
        setValue('dubaiSeller', resp.data.dubaiSeller)
        setValue('dubaiPerformanceCode', resp.data.dubaiPerformanceCode)
        setValue('ticketsEnabled', resp.data.ticketsEnabled)
        setValue('redeemTicketsEnabled', resp.data.redeemTicketsEnabled)
        setValue('useZipPassword', resp.data.useZipPassword || false)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    numberOfAllowedTransfers: yup.number().required(),
    upgradeInWalletEnabled: yup.boolean().required(),
    showTicketCode: yup.boolean().required(),
    dubaiIntegration: yup.boolean().required(),
    dubaiChannel: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    dubaiSeller: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    dubaiPerformanceCode: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    ticketsEnabled: yup.boolean().required(),
    redeemTicketsEnabled: yup.boolean().required(),
    useZipPassword: yup.boolean().required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<ITicketValues>({
    mode: 'onChange',
    resolver: yupResolver<ITicketValues>(schema),
    defaultValues: {
      upgradeInWalletEnabled: false,
      showTicketCode: false,
      ticketsEnabled: false,
      useZipPassword: false,
      dubaiIntegration: false,
      redeemTicketsEnabled: false,
    },
  })

  const submit = async (values: ITicketValues) => {
    try {
      const { status } = await updateSettingsTickets(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (ticket) {
      setValue('numberOfAllowedTransfers', ticket.numberOfAllowedTransfers)
      setValue('upgradeInWalletEnabled', ticket.upgradeInWalletEnabled)
      setValue('showTicketCode', ticket.showTicketCode)
      setValue('dubaiIntegration', ticket.dubaiIntegration)
      setValue('dubaiChannel', ticket.dubaiChannel)
      setValue('dubaiSeller', ticket.dubaiSeller)
      setValue('dubaiPerformanceCode', ticket.dubaiPerformanceCode)
      setValue('ticketsEnabled', ticket.ticketsEnabled)
      setValue('redeemTicketsEnabled', ticket.redeemTicketsEnabled)
      setValue('useZipPassword', ticket.useZipPassword)
    }
  }

  const dubaiIntergation = watch('dubaiIntegration')

  return (
    <>
      <h4 className="text-title-s text-accent-1">Tickets</h4>
      <Card className="p-4">
        <div className="p-3">
          <form id="settings-tickets-form" onSubmit={handleSubmit(submit)}>
            <div className="flex flex-col gap-2 space-y-2">
              <FormGroup label="Gift Transfer Count">
                <Input
                  isInvalid={!!errors?.numberOfAllowedTransfers?.message}
                  {...register('numberOfAllowedTransfers')}
                  type="number"
                  placeholder="Enter Gift Transfer Count"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup
                label="Upgrade Tickets from Wallet"
                direction="row"
                labelClassName="whitespace-nowrap"
              >
                <Controller
                  name="upgradeInWalletEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label="Show QR Code"
                direction="row"
                labelClassName="whitespace-nowrap"
              >
                <Controller
                  name="showTicketCode"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label="Dubai DET Government API"
                direction="row"
                labelClassName="whitespace-nowrap"
              >
                <Controller
                  name="dubaiIntegration"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              {dubaiIntergation && (
                <div className="p-5 bg-monochrome-5-50 rounded">
                  <FormGroup label="Channel">
                    <Input
                      isInvalid={!!errors?.dubaiChannel?.message}
                      {...register('dubaiChannel')}
                      type="text"
                      placeholder="Enter Channel"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Seller">
                    <Input
                      isInvalid={!!errors?.dubaiSeller?.message}
                      {...register('dubaiSeller')}
                      type="text"
                      placeholder="Enter Seller"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="PerformanceCode">
                    <Input
                      isInvalid={!!errors?.dubaiPerformanceCode?.message}
                      {...register('dubaiPerformanceCode')}
                      type="text"
                      placeholder="Enter Performance Code"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
              )}
              <FormGroup
                label="Enable Tickets Page"
                direction="row"
                labelClassName="whitespace-nowrap"
              >
                <Controller
                  name="ticketsEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label="Reedeem Tickets"
                direction="row"
                labelClassName="whitespace-nowrap"
              >
                <Controller
                  name="redeemTicketsEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label="Use ZIP Password for Invitations"
                direction="row"
                labelClassName="whitespace-nowrap"
              >
                <Controller
                  name="useZipPassword"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
            </div>
          </form>
        </div>
        <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-tickets-form"
            type="submit"
            variant="primary"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
